import React from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Divider,
  Rating,
  Chip,
} from '@mui/material';
import { Comment as CommentIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * ExecutionDetails Component
 * Expandable details section for an execution
 */
const ExecutionDetails = ({ execution, feedback }) => {
  return (
    <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
      <Grid container spacing={3}>
        {/* Formatted Prompt */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" gutterBottom>
            Formatted Prompt
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={execution.formatted_prompt || 'Not available'}
            variant="outlined"
            size="small"
            InputProps={{ readOnly: true }}
          />
        </Grid>

        {/* Raw Response */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" gutterBottom>
            Raw Response
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={execution.raw_response || 'Not available'}
            variant="outlined"
            size="small"
            InputProps={{ readOnly: true }}
          />
        </Grid>

        {/* Input Data */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" gutterBottom>
            Input Data
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={JSON.stringify(execution.input_data || {}, null, 2)}
            variant="outlined"
            size="small"
            InputProps={{ readOnly: true }}
          />
        </Grid>

        {/* Execution Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" gutterBottom>
            Execution Settings
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.100',
              borderRadius: 1,
            }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Temperature
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {execution.metadata?.llm_config?.temperature || 'Default'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Max Tokens
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {execution.metadata?.llm_config?.max_tokens ||
                    execution.metadata?.max_tokens ||
                    'Default'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Top P
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {execution.metadata?.llm_config?.top_p ||
                    execution.metadata?.top_p ||
                    'Default'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Frequency Penalty
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {execution.metadata?.llm_config?.frequency_penalty ||
                    execution.metadata?.frequency_penalty ||
                    'Default'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Presence Penalty
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {execution.metadata?.llm_config?.presence_penalty ||
                    execution.metadata?.presence_penalty ||
                    'Default'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  Output Format
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {execution.output_format || 'Default'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Structured Output */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" gutterBottom>
            Structured Output
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={(() => {
              // Use structured_output first, fallback to output_data
              const output =
                execution.structured_output ?? execution.output_data;
              const outputFormat = execution.output_format || 'text';

              // Handle null/undefined
              if (output == null) {
                return '';
              }

              // If output is already a string
              if (typeof output === 'string') {
                // Check if it's the problematic "[object Object]" string
                if (
                  output === '[object Object]' ||
                  output === '[object Array]'
                ) {
                  // Try to get the original object from the data
                  const originalOutput =
                    execution.structured_output ?? execution.output_data;
                  if (originalOutput && typeof originalOutput === 'object') {
                    try {
                      return JSON.stringify(originalOutput, null, 2);
                    } catch (e) {
                      return output;
                    }
                  }
                }
                return output;
              }

              // If output is an object or array, stringify it
              if (typeof output === 'object' && output !== null) {
                try {
                  return JSON.stringify(output, null, 2);
                } catch (e) {
                  // If stringification fails, try to get a better representation
                  if (Array.isArray(output)) {
                    return `[Array(${output.length})]`;
                  }
                  return `[Object with ${Object.keys(output).length} keys]`;
                }
              }

              // For other types, convert to string
              return String(output);
            })()}
            variant="outlined"
            size="small"
            InputProps={{ readOnly: true }}
          />
        </Grid>

        {/* Error Message */}
        {execution.error_message && (
          <>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom color="error">
                {execution.error_code || 'Error'}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={execution.error_message}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
                sx={{
                  '& .MuiInputBase-root': {
                    color: 'error.main',
                  },
                }}
              />
            </Grid>
            {execution.validation_errors &&
              execution.validation_errors.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" gutterBottom color="error">
                    Validation Errors ({execution.validation_errors.length})
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={JSON.stringify(execution.validation_errors, null, 2)}
                    variant="outlined"
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiInputBase-root': {
                        color: 'error.main',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                      },
                    }}
                  />
                </Grid>
              )}
          </>
        )}

        {/* Metadata */}
        {execution.metadata && Object.keys(execution.metadata).length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" gutterBottom>
              Metadata
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={JSON.stringify(execution.metadata, null, 2)}
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
            />
          </Grid>
        )}

        {/* Feedback Section */}
        {feedback && <FeedbackSection feedback={feedback} />}
      </Grid>
    </Box>
  );
};

/**
 * FeedbackSection Component
 * Displays user feedback for an execution
 */
const FeedbackSection = ({ feedback }) => {
  return (
    <Grid size={{ xs: 12 }}>
      <Divider sx={{ my: 2 }} />
      <Box
        sx={{
          p: 2,
          bgcolor: 'primary.50',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'primary.200',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          <CommentIcon color="primary" />
          <Typography variant="subtitle2" fontWeight="bold">
            User Feedback
          </Typography>
        </Box>
        <Box sx={{ mb: 1 }}>
          <Rating value={feedback.rating} readOnly size="small" />
        </Box>
        {feedback.feedback_text && feedback.feedback_text.trim() && (
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: 'text.secondary',
            }}
          >
            {feedback.feedback_text}
          </Typography>
        )}
        {feedback.categories && feedback.categories.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              mt: 1,
            }}
          >
            {feedback.categories.map((category, idx) => (
              <Chip
                key={`category-${idx}-${category}`}
                label={category}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            ))}
          </Box>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          {new Date(feedback.created_at).toLocaleString()}
        </Typography>
      </Box>
    </Grid>
  );
};

ExecutionDetails.propTypes = {
  execution: PropTypes.object.isRequired,
  feedback: PropTypes.object,
};

FeedbackSection.propTypes = {
  feedback: PropTypes.object.isRequired,
};

export default ExecutionDetails;
